import {
  Box,
  Typography,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import { tokens } from '../theme';
import { useTranslation } from '../hooks/useTranslation';
import dayjs from 'dayjs';

const ClientTable = ({ clients }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();

  const getScoreColor = (score) => {
    if (score > 700) return '#4caf50';
    if (score > 300) return '#ffc107';
    return '#f44336';
  };

  const getScoreBgColor = (score) => {
    if (score > 700) return 'rgba(76, 175, 80, 0.2)';
    if (score > 300) return 'rgba(255, 193, 7, 0.2)';
    return 'rgba(244, 67, 54, 0.2)';
  };

  return (
    <Box
      backgroundColor="#fff"
      borderRadius="12px"
      border="1px solid #272C3580"
      p={{ xs: '12px', md: '20px' }}
    >
      {/* Header */}
      <Typography
        variant="h5"
        fontWeight="600"
        color={colors.grey[100]}
        mb={{ xs: '12px', md: '20px' }}
        sx={{ fontSize: { xs: '16px', md: '20px' } }}
      >
        {t('dashboard.lastConsultedClients')}
      </Typography>

      {/* Table */}
      <TableContainer
        sx={{
          overflowX: 'auto',
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  color: colors.grey[300],
                  borderBottom: `1px solid ${colors.grey[700]}`,
                  fontSize: { xs: '0.75rem', md: '0.875rem' },
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  padding: { xs: '8px', md: '12px 16px' },
                }}
              >
                {t('dashboard.client').toUpperCase()}
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  color: colors.greenAccent[500],
                  borderBottom: `1px solid ${colors.grey[700]}`,
                  fontSize: { xs: '0.75rem', md: '0.875rem' },
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  padding: { xs: '8px', md: '12px 16px' },
                  whiteSpace: 'nowrap',
                }}
              >
                {t('dashboard.agroscore').toUpperCase()}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  color: colors.grey[300],
                  borderBottom: `1px solid ${colors.grey[700]}`,
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  padding: { xs: '8px', md: '16px' },
                  fontSize: { xs: '14px', md: '16px' },
                }}
              >
                {t('dashboard.date').toUpperCase()}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clients && clients.length > 0 ? (
              clients.map((client, index) => (
                <TableRow
                  key={index}
                  sx={{
                    '&:hover': {
                      backgroundColor: colors.grey[900],
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      color: colors.grey[100],
                      borderBottom: `none`,
                      padding: '16px',
                    }}
                  >
                    {client.name}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      borderBottom: `none`,
                      padding: '16px',
                    }}
                  >
                    <Chip
                      label={Math.floor(client.score)}
                      sx={{
                        backgroundColor: getScoreBgColor(client.score),
                        color: getScoreColor(client.score),
                        fontWeight: 'bold',
                        borderRadius: '4px',
                        border: `1px solid ${getScoreColor(client.score)}`,
                        fontSize: { xs: '0.75rem', md: '1rem' },
                        minWidth: { xs: '44px', md: '60px' },
                        height: { xs: '28px', md: '32px' },
                      }}
                    />
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: colors.grey[100],
                      borderBottom: `none`,
                      padding: { xs: '8px', md: '16px' },
                      fontSize: { xs: '13px', md: '16px' },
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {dayjs(client.date).format('DD/MM/YYYY')}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={3}
                  align="center"
                  sx={{
                    color: colors.grey[300],
                    borderBottom: `1px solid ${colors.grey[700]}`,
                    padding: '32px',
                  }}
                >
                  {t('dashboard.NoClientConsulted')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ClientTable;
